import React from "react";
import styled from "styled-components";
import { HashCode } from "../HashCode";

const DeviceInfoWrapper = styled.div`
  dl {
    display: grid;
    grid-template-columns: 1fr;
    gap: 8px;
    margin: 0;
  }

  dt {
    font-weight: 600;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.textMuted};
    font-size: 12px;
    display: flex;
    align-items: center;
    margin-bottom: 2px;
  }

  dd {
    margin: 0;
    color: ${({ theme }) => theme.colors.text};
    font-weight: 500;
    word-break: break-word;
    font-size: 14px;
    display: flex;
    align-items: center;
    margin-bottom: 16px;
  }

  .monospace {
    font-family: ${({ theme }) => theme.fontFamily.mono};
  }

  .section-divider {
    grid-column: 1 / -1;
    height: 1px;
    background-color: ${({ theme }) => theme.colors.border};
    margin: 16px 0;
  }

  .section-title {
    grid-column: 1 / -1;
    color: ${({ theme }) => theme.colors.verae[500]};
    font-size: 14px;
    font-weight: 500;
    margin-top: 16px;
    margin-bottom: 8px;
  }
`;

interface DeviceInfoProps {
  deviceInfo: {
    [key: string]: any;
  };
  formatKey: (key: string) => string;
  blockchainData?: {
    index: number;
    hash: string;
    timestamp?: string;
  };
}

export const DeviceInfo: React.FC<DeviceInfoProps> = ({
  deviceInfo,
  formatKey,
  blockchainData,
}) => {
  // Extract priority fields to display at the top
  const title = deviceInfo.title;
  const serialNumber = deviceInfo.serialNumber || deviceInfo.identifier;

  // Create a filtered deviceInfo object without the priority fields
  const filteredDeviceInfo = { ...deviceInfo };
  if (title) {
    delete filteredDeviceInfo.title;
  }
  if (serialNumber) {
    delete filteredDeviceInfo.serialNumber;
    delete filteredDeviceInfo.identifier;
  }

  return (
    <DeviceInfoWrapper>
      <dl>
        {/* Prioritize title and serial number at the top */}
        {title && (
          <React.Fragment>
            <dt>{formatKey("title")}</dt>
            <dd>{title}</dd>
          </React.Fragment>
        )}

        {serialNumber && (
          <React.Fragment>
            <dt>
              {formatKey(
                deviceInfo.serialNumber ? "serialNumber" : "identifier"
              )}
            </dt>
            <dd>{serialNumber}</dd>
          </React.Fragment>
        )}

        {/* Display the rest of the device info */}
        {Object.entries(filteredDeviceInfo).map(([key, value]) => {
          if (key === "aiEnhanced" || key === "confidence") return null;

          // Handle different value types
          let displayValue: React.ReactNode = "Unknown";

          if (value === null || value === undefined) {
            displayValue = "Unknown";
          } else if (typeof value === "boolean") {
            displayValue = value ? "Yes" : "No";
          } else if (typeof value === "number" && key === "confidence") {
            displayValue = `${value}%`;
          } else if (typeof value === "object") {
            displayValue = JSON.stringify(value);
          } else {
            displayValue = String(value);
          }

          return (
            <React.Fragment key={key}>
              <dt>{formatKey(key)}</dt>
              <dd>{displayValue}</dd>
            </React.Fragment>
          );
        })}

        {blockchainData && (
          <>
            <dt>Block Index</dt>
            <dd>{blockchainData.index}</dd>

            <dt>Block Hash</dt>
            <dd>
              <HashCode hash={blockchainData.hash} />
            </dd>

            {blockchainData.timestamp && (
              <>
                <dt>Block Timestamp</dt>
                <dd>{blockchainData.timestamp}</dd>
              </>
            )}
          </>
        )}
      </dl>
    </DeviceInfoWrapper>
  );
};
